import {
  CATCH_IMAGE_NORMALIZATION_ERROR_CODE,
  CATCH_IMAGE_NORMALIZATION_ERROR_MESSAGE,
  buildCatchMediaClientError,
  buildUploadFilename,
  normalizeBlobMimeType,
  normalizeMimeType,
  replaceFilenameExtension,
} from "./catchMediaConstants";

const loadImageElementForUpload = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Missing image source for upload normalization"));
      return;
    }

    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Could not decode image for upload normalization"));
    image.src = src;
  });

export const buildObjectPreviewUrl = (file) => {
  if (typeof URL?.createObjectURL !== "function") {
    return "";
  }

  try {
    return URL.createObjectURL(file);
  } catch (error) {
    console.warn("Could not create object URL preview for selected media:", error);
    return "";
  }
};

export const buildDownscaledPreviewUrl = async (
  file,
  maxDimension = 1200
) => {
  if (!file || typeof document === "undefined") {
    return "";
  }

  let renderedImage = null;
  let objectUrlToRevoke = "";

  try {
    renderedImage =
      typeof createImageBitmap === "function"
        ? await createImageBitmap(file).catch(() => null)
        : null;

    const resolvedImage =
      renderedImage ||
      (await loadImageElementForUpload(
        objectUrlToRevoke || (objectUrlToRevoke = buildObjectPreviewUrl(file))
      ));

    const width =
      renderedImage?.width ||
      resolvedImage?.naturalWidth ||
      resolvedImage?.width ||
      0;
    const height =
      renderedImage?.height ||
      resolvedImage?.naturalHeight ||
      resolvedImage?.height ||
      0;

    if (!width || !height) {
      return "";
    }

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      return "";
    }

    context.drawImage(resolvedImage, 0, 0, targetWidth, targetHeight);

    const previewBlob = await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob || null), "image/jpeg", 0.85);
    });

    if (!previewBlob) {
      return "";
    }

    return buildObjectPreviewUrl(previewBlob);
  } catch (error) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "downscaledPreview",
      errorName: error?.name,
      errorMessage: error?.message,
    });
    return "";
  } finally {
    if (renderedImage?.close) {
      renderedImage.close();
    }
    if (objectUrlToRevoke) {
      URL.revokeObjectURL(objectUrlToRevoke);
    }
  }
};

export const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (typeof FileReader === "undefined") {
      resolve("");
      return;
    }

    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () =>
      reject(reader.error || new Error("Could not read selected media as data URL"));
    reader.readAsDataURL(file);
  });

export const buildPreviewSource = async (
  file,
  mediaType = "image",
  options = {}
) => {
  const previewInputKind =
    typeof options.previewInputKind === "string"
      ? options.previewInputKind
      : "snapshot";
  const selectionId =
    Number.isFinite(options.selectionId) ? options.selectionId : null;
  let previewUrl = "";
  let previewSource = "";
  let previewStatus = "failed";

  console.log("[CATCH_IMG_DEBUG]", {
    stage: "preview_source_chosen",
    selectionId,
    mediaType,
    previewInputKind,
    hasFile: Boolean(file),
  });

  if (mediaType === "image" && previewInputKind !== "snapshot") {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "preview_placeholder_used",
      selectionId,
      mediaType,
      previewInputKind,
      reason: "snapshot_unavailable",
    });
    return {
      url: "",
      previewSource: "placeholder",
      previewStatus: "placeholder",
    };
  }

  try {
    if (mediaType === "image") {
      const downscaledUrl = await buildDownscaledPreviewUrl(file);
      if (downscaledUrl) {
        previewUrl = downscaledUrl;
        previewSource = "downscaled-url";
      }
    }

    if (!previewUrl) {
      previewUrl = buildObjectPreviewUrl(file);
      previewSource = previewUrl ? "object-url" : "";
    }

    if (!previewUrl && mediaType === "image") {
      try {
        previewUrl = await readFileAsDataUrl(file);
        previewSource = previewUrl ? "data-url" : previewSource;
      } catch (error) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "preview_generation_failed_nonblocking",
          selectionId,
          mediaType,
          previewInputKind,
          previewSourceAttempt: "data-url",
          errorName: error?.name,
          errorMessage: error?.message,
        });
      }
    }
  } catch (error) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "preview_generation_failed_nonblocking",
      selectionId,
      mediaType,
      previewInputKind,
      errorName: error?.name,
      errorMessage: error?.message,
    });
  }

  if (previewUrl) {
    previewStatus = "ready";
    console.log("[CATCH_IMG_DEBUG]", {
      stage: "preview_generated",
      selectionId,
      mediaType,
      previewInputKind,
      previewSource,
      urlKind:
        typeof previewUrl === "string" && previewUrl.startsWith("data:")
          ? "data-url"
          : typeof previewUrl === "string" && previewUrl.startsWith("blob:")
            ? "blob-url"
            : "other",
    });
    return {
      url: previewUrl,
      previewSource,
      previewStatus,
    };
  }

  console.warn("[CATCH_IMG_DEBUG]", {
    stage: "preview_generation_failed_nonblocking",
    selectionId,
    mediaType,
    previewInputKind,
    errorName: "PreviewUnavailable",
    errorMessage: "No preview source could be generated",
  });

  if (mediaType === "image") {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "preview_placeholder_used",
      selectionId,
      mediaType,
      previewInputKind,
      reason: "preview_generation_failed",
    });
    return {
      url: "",
      previewSource: "placeholder",
      previewStatus: "placeholder",
    };
  }

  return {
    url: previewUrl,
    previewSource,
    previewStatus,
  };
};

export const normalizeImageFileForCatchMedia = async (
  file,
  options = {}
) => {
  const filename = buildUploadFilename(
    { file, name: options.filename },
    "image",
    normalizeMimeType(file?.type)
  );
  let renderedImage = null;
  let objectUrlToRevoke = "";
  let usedImgFallback = false;

  try {
    if (typeof createImageBitmap === "function") {
      try {
        renderedImage = await createImageBitmap(file);
        console.log("[CATCH_IMG_DEBUG]", {
          stage: "createImageBitmap",
          succeeded: true,
          width: renderedImage?.width,
          height: renderedImage?.height,
        });
      } catch (bitmapError) {
        renderedImage = null;
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "createImageBitmap",
          succeeded: false,
          errorName: bitmapError?.name,
          errorMessage: bitmapError?.message,
        });
      }
    } else {
      console.log("[CATCH_IMG_DEBUG]", {
        stage: "createImageBitmap",
        succeeded: false,
        errorName: "Unsupported",
        errorMessage: "createImageBitmap not available",
      });
    }

    let resolvedImage = renderedImage;
    if (!resolvedImage) {
      usedImgFallback = true;
      try {
        resolvedImage = await loadImageElementForUpload(
          options.preferredSrc || (objectUrlToRevoke = buildObjectPreviewUrl(file))
        );
        console.log("[CATCH_IMG_DEBUG]", {
          stage: "imgDecode",
          usedFallback: true,
          succeeded: true,
          naturalWidth: resolvedImage?.naturalWidth,
          naturalHeight: resolvedImage?.naturalHeight,
          resolvedFromObjectUrl: Boolean(objectUrlToRevoke),
        });
      } catch (imgError) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "imgDecode",
          usedFallback: true,
          succeeded: false,
          errorName: imgError?.name,
          errorMessage: imgError?.message,
        });
        throw imgError;
      }
    } else {
      console.log("[CATCH_IMG_DEBUG]", {
        stage: "imgDecode",
        usedFallback: false,
        succeeded: true,
      });
    }

    const width =
      renderedImage?.width ||
      resolvedImage?.naturalWidth ||
      resolvedImage?.width ||
      0;
    const height =
      renderedImage?.height ||
      resolvedImage?.naturalHeight ||
      resolvedImage?.height ||
      0;

    if (!width || !height) {
      throw new Error("Decoded image has no dimensions");
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    console.log("[CATCH_IMG_DEBUG]", {
      stage: "canvas",
      width,
      height,
      totalPixels: width * height,
      over16MP: width * height > 16777216,
      contextAvailable: Boolean(context),
    });
    if (!context) {
      throw new Error("Could not create upload normalization canvas");
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(resolvedImage, 0, 0, width, height);

    const normalizedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          console.log("[CATCH_IMG_DEBUG]", {
            stage: "toBlob",
            blobExists: Boolean(blob),
            blobType: blob?.type,
            blobSize: blob?.size,
          });
          if (blob) {
            resolve(blob);
            return;
          }

          reject(new Error("Could not encode JPEG upload payload"));
        },
        "image/jpeg",
        0.92
      );
    });

    const normalizedFilename = replaceFilenameExtension(filename, "jpg");
    const normalizedFile =
      typeof File === "function"
        ? new File([normalizedBlob], normalizedFilename, {
            type: "image/jpeg",
            lastModified: Number.isFinite(file?.lastModified)
              ? file.lastModified
              : Date.now(),
          })
        : normalizeBlobMimeType(normalizedBlob, "image/jpeg");

    console.log("[CATCH_IMG_DEBUG]", {
      stage: "toBlob",
      preparedFilename: normalizedFilename,
      preparedFileType: normalizedFile?.type,
      preparedFileSize: normalizedFile?.size,
    });

    return {
      file: normalizedFile,
      filename: normalizedFilename,
      mimeType: "image/jpeg",
    };
  } catch (error) {
    console.error("[CATCH_IMG_DEBUG]", {
      stage: "normalizationError",
      usedImgFallback,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
      causeName: error?.cause?.name,
      causeMessage: error?.cause?.message,
    });
    throw buildCatchMediaClientError(
      CATCH_IMAGE_NORMALIZATION_ERROR_MESSAGE,
      CATCH_IMAGE_NORMALIZATION_ERROR_CODE,
      error
    );
  } finally {
    if (renderedImage?.close) {
      renderedImage.close();
    }

    if (objectUrlToRevoke) {
      URL.revokeObjectURL(objectUrlToRevoke);
    }
  }
};

export const revokePreviewOwnedUrl = (url) => {
  if (
    typeof url !== "string" ||
    !url ||
    typeof URL?.revokeObjectURL !== "function" ||
    !url.startsWith("blob:")
  ) {
    return;
  }

  try {
    URL.revokeObjectURL(url);
  } catch (error) {
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "preview_revoke_failed_nonblocking",
      errorName: error?.name,
      errorMessage: error?.message,
    });
  }
};
